// Playing area bookings, club admin side (BOOKINGS_PLAN.md §5). Pure maths and
// words: the hours painter (bands → blocks on a week grid, overlap checks,
// drag → band), the who-can-book table (inherited cells, request rows), the
// settings choices and the admin calendar's row building. No network here;
// the API's own lines (hoursLine, whoCanBookLine) are shown as they arrive.

import {queryFlag} from './queryFlags.js';
import {slotRows} from './bookingRows.js';

// ── Band kinds (B3) ──
// X53: a playing area's optional short description ("Outdoor, floodlit, savannah") is one muted line under its
// name in a booking table's column header, so it is capped where the API caps it: 28 characters, measured to fit
// the admin calendar's 176px column at the header's small size (30 overflowed). Shown cut with a tooltip past that.
export const AREA_DESCRIPTION_MAX = 28;

export const WINDOW_KIND = {bookingNeeded: 'BookingNeeded', matchesOnly: 'MatchesOnly', closed: 'Closed'};

export const WINDOW_KINDS = [
  {value: WINDOW_KIND.bookingNeeded, label: 'Booking needed', help: 'Players book a slot ahead. One nobody has booked can still be used.'},
  {value: WINDOW_KIND.closed, label: 'Closed', help: 'Nobody can play. For one-offs, freeze the area instead.'},
  // Third and low-key on purpose: league nights are the rare case (B3).
  {value: WINDOW_KIND.matchesOnly, label: 'Matches only', help: 'Only matches and admins can use the areas, booked or not. For league nights.'},
];

export function kindLabel(kind) {
  return WINDOW_KINDS.find(k => k.value === kind)?.label ?? kind;
}

// ── Days (1 = Monday … 7 = Sunday, as the API) ──
export const DAYS = [
  {n: 1, short: 'Mon', two: 'Mo', letter: 'M', long: 'Monday'},
  {n: 2, short: 'Tue', two: 'Tu', letter: 'T', long: 'Tuesday'},
  {n: 3, short: 'Wed', two: 'We', letter: 'W', long: 'Wednesday'},
  {n: 4, short: 'Thu', two: 'Th', letter: 'T', long: 'Thursday'},
  {n: 5, short: 'Fri', two: 'Fr', letter: 'F', long: 'Friday'},
  {n: 6, short: 'Sat', two: 'Sa', letter: 'S', long: 'Saturday'},
  {n: 7, short: 'Sun', two: 'Su', letter: 'S', long: 'Sunday'},
];

export function daysFromMask(mask) {
  const days = [];
  for (let d = 1; d <= 7; d++) if (mask & (1 << (d - 1))) days.push(d);
  return days;
}

export function maskFromDays(days) {
  return (days ?? []).reduce((m, d) => m | (1 << (d - 1)), 0);
}

/** "Mon–Fri", "Sat, Sun", "Every day", "Mon, Wed–Fri". */
export function daysWords(days) {
  const sorted = [...new Set(days ?? [])].sort((a, b) => a - b);
  if (sorted.length === 7) return 'Every day';
  if (sorted.length === 0) return 'No days';
  const parts = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++;
    const a = DAYS[sorted[i] - 1].short;
    const b = DAYS[sorted[j] - 1].short;
    parts.push(j - i >= 2 ? `${a}–${b}` : j === i ? a : `${a}, ${b}`);
    i = j + 1;
  }
  return parts.join(', ');
}

// ── Times ("HH:mm" local) ──
export function minutesOf(hhmm) {
  if (!hhmm) return 0;
  const [h, m] = String(hhmm).split(':').map(n => parseInt(n, 10) || 0);
  return h * 60 + m;
}

export function toHhmm(minutes) {
  const m = ((minutes % 1440) + 1440) % 1440;
  return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
}

/** 1080 → "6pm", 1110 → "6:30pm", 0 → "12am", 1440 → "midnight". */
export function timeWord(minutes) {
  if (minutes === 1440 || minutes === 0) return minutes === 1440 ? 'midnight' : '12am';
  const h24 = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const suffix = h24 < 12 ? 'am' : 'pm';
  return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, '0')}${suffix}`;
}

/** "6pm–11pm"; a band that ends at or before its start runs to the next day (BE15). */
export function bandWord(startLocal, endLocal) {
  const s = minutesOf(startLocal);
  let e = minutesOf(endLocal);
  if (e <= s) e += 1440;
  // X202 (R82 L14): 0:00 to midnight is the whole day ("Tue all day"), never "12am–midnight".
  if (s === 0 && e === 1440) return 'all day';
  return `${timeWord(s)}–${timeWord(e === 1440 ? 1440 : e % 1440 === 0 ? 1440 : e % 1440)}`;
}

/** X94 (R35 L2): bandWord on the grid's 24-hour clock, for the hours painter: "18:00–23:00", "22:00–24:00". */
export function bandWord24(startLocal, endLocal) {
  const s = minutesOf(startLocal);
  let e = minutesOf(endLocal);
  if (e <= s) e += 1440;
  return periodWord(s, e);
}

export function bandEndsNextDay(window) {
  return minutesOf(window.endLocal) <= minutesOf(window.startLocal);
}

/** "40 min" / "1 h" / "1 h 20" / "2 h" / "All day". X78 (R27 N10): one duration style everywhere, the calendar's (bookingDrag.durationWords, the API's ShortDuration). */
export function durationWord(minutes, allDay = false) {
  if (allDay || minutes == null) return 'All day';
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r} min`;
  return r ? `${h} h ${String(r).padStart(2, '0')}` : `${h} h`;
}

export function cutoffWord(minutes) {
  if (minutes == null) return '';
  if (minutes === 0) return 'Any time';
  if (minutes < 60) return `${minutes} min before`;
  if (minutes < 1440) return minutes % 60 === 0 ? `${minutes / 60} hour${minutes === 60 ? '' : 's'} before` : `${minutes} min before`;
  const d = minutes / 1440;
  return Number.isInteger(d) ? `${d} day${d === 1 ? '' : 's'} before` : `${Math.round(minutes / 60)} hours before`;
}

// ── The painter (B3, BE15) ──
export const PAINTER_ROW_MINUTES = 30;

/**
 * Each band on each of its days as a block on the week grid, in minutes from
 * midnight. A band ending at or before its start spills into the next day:
 * one block to midnight on its day and one from midnight on the day after
 * (Sunday → Monday). `index` is the band's position in `windows`.
 */
export function paintBlocks(windows) {
  const blocks = [];
  (windows ?? []).forEach((w, index) => {
    const start = minutesOf(w.startLocal);
    const end = minutesOf(w.endLocal);
    const days = w.days?.length ? w.days : daysFromMask(w.daysMask ?? 0);
    const wraps = end <= start;
    for (const day of days) {
      blocks.push({index, day, startMin: start, endMin: wraps ? 1440 : end, kind: w.kind, spill: false, continues: wraps && end > 0});
      if (wraps && end > 0) blocks.push({index, day: day === 7 ? 1 : day + 1, startMin: 0, endMin: end, kind: w.kind, spill: true, continues: false});
    }
  });
  return blocks;
}

/** The two bands cover the same minute on the same day (the API refuses the save). */
export function windowsOverlap(a, b) {
  const blocksA = paintBlocks([a]);
  const blocksB = paintBlocks([b]);
  return blocksA.some(x => blocksB.some(y => x.day === y.day && x.startMin < y.endMin && y.startMin < x.endMin));
}

/** The first overlapping pair, or null. */
export function findOverlap(windows) {
  const list = windows ?? [];
  for (let i = 0; i < list.length; i++)
    for (let j = i + 1; j < list.length; j++)
      if (windowsOverlap(list[i], list[j])) return {i, j};
  return null;
}

/** Drag from one row to another on a day → a band (rows are PAINTER_ROW_MINUTES tall, row 0 = midnight). */
export function bandFromDrag(day, rowA, rowB, kind = WINDOW_KIND.bookingNeeded, rowMinutes = PAINTER_ROW_MINUTES, step = null) {
  const from = Math.min(rowA, rowB);
  const to = Math.max(rowA, rowB);
  const startMin = from * rowMinutes;
  // X94 (R35 M1): with the set's step, a painted band is whole slots long (rounded up), so its slots end on the band.
  const swept = (to + 1 - from) * rowMinutes;
  const s = Number(step) || 0;
  const length = s > 0 ? Math.max(s, Math.ceil(swept / s) * s) : swept;
  const endMin = Math.min(1440, startMin + length);
  return {days: [day], startLocal: toHhmm(startMin), endLocal: toHhmm(endMin), kind};
}

/** Which blocks a new band would collide with; a tidy painter refuses the drop rather than saving a refusal. */
export function blocksHit(windows, candidate, ignoreIndex = null) {
  return (windows ?? []).map((w, i) => ({w, i})).filter(({w, i}) => i !== ignoreIndex && windowsOverlap(w, candidate)).map(({i}) => i);
}

export function windowsToRequest(windows) {
  return (windows ?? []).map(w => ({
    days: w.days?.length ? [...w.days] : daysFromMask(w.daysMask ?? 0),
    startLocal: w.startLocal,
    // X202 (R82 L14): an end at midnight is sent as "24:00" (the end of the day): a weekly "Closed all day" band is
    // 00:00–24:00, which the API keeps as the whole day (it refused 00:00–00:00 as starting and ending at once).
    endLocal: minutesOf(w.endLocal) === 0 ? '24:00' : w.endLocal,
    kind: w.kind,
  }));
}

export function bandSummary(window) {
  const days = window.days?.length ? window.days : daysFromMask(window.daysMask ?? 0);
  return `${daysWords(days)} ${bandWord(window.startLocal, window.endLocal)}`;
}

/** The painter's row labels: every hour from midnight, "12am" … "11pm". */
export function painterHours() {
  return Array.from({length: 24}, (_, h) => ({hour: h, label: timeWord(h * 60)}));
}

/** 15-minute choices for the band dialog's From / To (To offers midnight at the end). */
export function timeChoices(step = 15, includeMidnightEnd = false) {
  const out = [];
  for (let m = 0; m < 1440; m += step) out.push({value: toHhmm(m), title: timeWord(m)});
  if (includeMidnightEnd) out.push({value: '00:00', title: 'midnight'});
  return out;
}

/** X94 (R35 L2): timeChoices on the grid's 24-hour clock ("8:00", "14:15"; "24:00" for the midnight end). */
export function timeChoices24(step = 15, includeMidnightEnd = false) {
  return timeChoices(step, includeMidnightEnd).map((o, i, all) => ({value: o.value, title: includeMidnightEnd && i === all.length - 1 && o.value === '00:00' ? '24:00' : clockWord(o.value)}));
}

// ── Group settings ──
export const SLOT_OPTIONS = [
  {value: 30, title: '30 min'},
  {value: 40, title: '40 min'},
  {value: 45, title: '45 min'},
  {value: 60, title: '1 hour'},
  {value: 90, title: '90 min'},
  {value: 120, title: '2 hours'},
];

export const CUTOFF_OPTIONS = [0, 30, 60, 120, 180, 360, 720, 1440, 2880].map(v => ({value: v, title: v === 0 ? 'Any time' : cutoffWord(v)}));

/** Longest booking choices for a group: whole slots up to 8 hours, then All day. */
export function durationOptions(slotMinutes) {
  const step = slotMinutes > 0 ? slotMinutes : 60;
  const out = [];
  for (let m = step; m <= 480; m += step) out.push({value: m, title: durationWord(m)});
  out.push({value: 'allDay', title: 'All day'});
  return out;
}

export const DAYS_AHEAD_OPTIONS = [0, 1, 2, 3, 5, 7, 10, 14, 21, 28, 42, 56, 90, 180, 365].map(d => ({value: d, title: d === 0 ? 'Same day only' : d === 1 ? '1 day' : `${d} days`}));

export const AT_ONCE_OPTIONS = [...[1, 2, 3, 4, 5, 6, 8, 10].map(n => ({value: n, title: String(n)})), {value: 'any', title: 'Any'}];

// ── Who can book (B4b) ──
export const AUDIENCE = {noMembership: 'NoMembership', membershipType: 'MembershipType', guest: 'Guest', visitor: 'Visitor'};

/**
 * The small line under a row's name. A membership type that isn't live says
 * so ("Draft · 3 members"), which also tells two types with one name apart
 * (TEST_T2 Low 14); `typeStatuses` maps a type id to its status label.
 */
export function audienceHelp(row, memberCounts = {}, typeStatuses = {}) {
  switch (row.audience) {
    case AUDIENCE.noMembership: return 'Everyone in the club without one';
    case AUDIENCE.visitor: return 'No account · your booking page · one booking at a time';
    case AUDIENCE.guest: return "Added to someone's booking";
    default: {
      const n = memberCounts[row.membershipTypeId];
      const status = typeStatuses[row.membershipTypeId];
      return [status && status !== 'Live' ? status : null, n == null ? null : `${n} member${n === 1 ? '' : 's'}`].filter(Boolean).join(' · ');
    }
  }
}

/** The editable shape of one row: a cell is its own value or null (inherited from No membership, shown grey). */
export function editableRow(row) {
  const maxDuration = row.allDay ? 'allDay' : row.maxDurationMinutes ?? null;
  return {
    id: row.id ?? null,
    audience: row.audience,
    membershipTypeId: row.membershipTypeId ?? null,
    label: row.label ?? '',
    canBook: row.canBook ?? null,
    maxDuration,
    daysAhead: row.daysAhead ?? null,
    // The API: 0 = no limit ("Any"), null = copy the No membership row.
    maxActiveBookings: row.maxActiveBookings === 0 ? 'any' : row.maxActiveBookings ?? null,
    priceMinor: row.priceMinor ?? null,
    effective: row.effective ?? {},
  };
}

/** What a cell shows: the row's own value, or the No membership row's (inherited). */
export function cellValue(row, field, noMembershipRow) {
  const own = row[field];
  if (row.audience === AUDIENCE.noMembership) return {value: own, inherited: false};
  if (own !== null && own !== undefined) return {value: own, inherited: false};
  return {value: noMembershipRow ? noMembershipRow[field] : null, inherited: true};
}

/** Guests only have Can book and Price (B4b); a visitor always has one booking at a time (BE4), so no at-once cell. */
export function cellApplies(row, field) {
  if (row.audience === AUDIENCE.visitor) return field !== 'maxActiveBookings';
  if (row.audience !== AUDIENCE.guest) return true;
  return field === 'canBook' || field === 'priceMinor';
}

/**
 * BE11: the chips a meet's "Hold playing areas" block offers: the club's
 * areas for the meet's sport (an area with no sports plays any), plus any
 * area the meet already holds that no longer lists the sport, flagged so it
 * can still be unticked (otherwise the hold could never be undone).
 */
export function heldAreaChoices(areas, sportId, heldIds = []) {
  const held = new Set(heldIds ?? []);
  const sportsOf = (a) => (Array.isArray(a.sportIds) ? a.sportIds : (a.sportId ? [a.sportId] : []));
  const forSport = (a) => !sportId || !sportsOf(a).length || sportsOf(a).includes(sportId);
  return [...(areas ?? [])]
      .filter(a => forSport(a) || held.has(a.id))
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || String(a.name).localeCompare(String(b.name)))
      .map(a => ({id: a.id, name: a.name, stale: !forSport(a), label: forSport(a) ? a.name : `${a.name} · no longer for this sport`}));
}

/** The table in the mockup's order: No membership, each membership type, Visitors, Guests; missing audiences get an empty row. */
export function tableRows(whoCanBook, membershipTypes = []) {
  const rows = (whoCanBook ?? []).map(editableRow);
  const find = (audience, typeId = null) => rows.find(r => r.audience === audience && (audience !== AUDIENCE.membershipType || r.membershipTypeId === typeId));
  const out = [];
  out.push(find(AUDIENCE.noMembership) ?? editableRow({audience: AUDIENCE.noMembership, label: 'No membership', canBook: true, daysAhead: 7, maxDurationMinutes: 120}));
  const seen = new Set();
  for (const t of membershipTypes) {
    seen.add(t.id);
    out.push(find(AUDIENCE.membershipType, t.id) ?? editableRow({audience: AUDIENCE.membershipType, membershipTypeId: t.id, label: t.name}));
  }
  for (const r of rows) if (r.audience === AUDIENCE.membershipType && !seen.has(r.membershipTypeId)) out.push(r);
  out.push(find(AUDIENCE.visitor) ?? editableRow({audience: AUDIENCE.visitor, label: 'Visitors', canBook: false}));
  out.push(find(AUDIENCE.guest) ?? editableRow({audience: AUDIENCE.guest, label: 'Guests', canBook: false}));
  return out;
}

export function rowsToRequest(rows) {
  return (rows ?? []).map(r => ({
    audience: r.audience,
    membershipTypeId: r.membershipTypeId ?? null,
    canBook: r.canBook ?? null,
    maxDurationMinutes: r.maxDuration === 'allDay' || r.maxDuration == null ? null : r.maxDuration,
    allDay: r.maxDuration === 'allDay',
    daysAhead: r.daysAhead ?? null,
    maxActiveBookings: r.maxActiveBookings === 'any' ? 0 : r.maxActiveBookings ?? null,
    priceMinor: r.priceMinor ?? null,
  }));
}

/** "2 hours, 7 days ahead, 2 at once · £4 a slot" for a resolved row. */
export function allowanceLine(a, money) {
  if (!a) return '';
  if (a.canBook === false) return "Can't book";
  const parts = [durationWord(a.maxDurationMinutes, a.allDay), a.daysAhead === 0 ? 'same day only' : `${a.daysAhead} day${a.daysAhead === 1 ? '' : 's'} ahead`, a.maxActiveBookings == null ? 'no limit at once' : `${a.maxActiveBookings} at once`];
  return parts.join(', ') + ' · ' + (a.priceMinor ? money(a.priceMinor) + ' a slot' : 'free');
}

// ── Club-wide settings (B7b, B7d, BE9) ──
export const MATCH_OPENS_OPTIONS = [
  {value: 'players', title: 'Same as the players', help: 'The more generous of the two sides\' windows.'},
  {value: 'days:7', title: '7 days ahead'},
  {value: 'days:14', title: '14 days ahead'},
  {value: 'days:28', title: '28 days ahead'},
  {value: 'deadline', title: 'Up to the match deadline'},
];

export function matchOpensKey(settings) {
  if (!settings) return 'players';
  if (settings.matchBookingOpens === 'days') return 'days:' + (settings.matchBookingDays ?? 7);
  return settings.matchBookingOpens ?? 'players';
}

export function matchOpensRequest(key) {
  if (key?.startsWith('days:')) return {matchBookingOpens: 'days', matchBookingDays: parseInt(key.slice(5), 10)};
  return {matchBookingOpens: key, matchBookingDays: null};
}

export const MATCH_PAYER_OPTIONS = [
  {value: 'OnePayer', title: 'One person pays', help: 'Whoever confirms the time picks who, from the match\'s players.'},
  {value: 'Split', title: 'Split between the sides', help: 'Each side pays its share; card fees are charged per payment.'},
];

/** The API says CONFIRM_NEEDED / CHOICE_NEEDED in front of a refusal that a confirm step answers. */
export function parseRefusal(message) {
  const text = String(message ?? '');
  const m = text.match(/^(CONFIRM_NEEDED|CHOICE_NEEDED):\s*(.*)$/s);
  if (!m) return {kind: 'error', message: text};
  return {kind: m[1] === 'CONFIRM_NEEDED' ? 'confirm' : 'choice', message: m[2]};
}

// ── Freezes (B9) ──
export const FREEZE_REASONS = [
  {value: 'Maintenance', title: 'Maintenance'},
  {value: 'Tournament', title: 'Tournament'},
  {value: 'PrivateEvent', title: 'Private event'},
  {value: 'Other', title: 'Other'},
];

// ── The admin calendar (BookCalendar) ──
export const CELL_STATUS_CLASS = {
  free: 'bk-cell--free',
  booked: 'bk-cell--booked',
  mine: 'bk-cell--booked',
  match: 'bk-cell--match',
  matchesOnly: 'bk-cell--matchesonly',
  open: 'bk-cell--open',
  frozen: 'bk-cell--frozen',
  closed: 'bk-cell--closed',
  notYetOpen: 'bk-cell--free',
};

/**
 * X94 (R35 M1): the row a start instant sits in (the last row starting at or before it; 0 before the first) and the
 * row boundary an end instant reaches (the first row starting at or after it; the row count past the last), on rows
 * that need not be the same length (see calendarGrid).
 */
export function gridRowAt(rows, iso, {end = false} = {}) {
  const list = rows ?? [];
  if (!list.length) return 0;
  const t = typeof iso === 'number' ? iso : new Date(iso).getTime();
  const at = (r) => r.time ?? new Date(r.startsAt).getTime();
  if (end) {
    const i = list.findIndex((r) => at(r) >= t);
    return i < 0 ? list.length : i;
  }
  let i = 0;
  while (i + 1 < list.length && at(list[i + 1]) <= t) i++;
  return i;
}

/**
 * One day of the availability response → grid rows, with each area's cells merged into blocks (a two-slot booking is
 * one block spanning two rows).
 *
 * X94 (R35 M1, H2): the rows ARE the API's slots: one row per slot start (an hours change off the step, 14:30 on a
 * 40-minute set, gives a 14:30 row, never a made-up 14:40 one), and a gap between the day's slots (the booking rules'
 * own open play, 13:20–14:40 on R25 Boards) is filled with rows on the step from the slot before it, so the time column
 * tells the truth and the gap is drawn: as Open play (the weekly open time, `weekly`), the block /book draws there. Rows
 * carry their own end (`end`, ms), so a short gap row ends where the next slot starts. Groups with different slot
 * lengths share the axis by their slot starts.
 */
export function calendarGrid(day, areas, groups) {
  const groupSlots = new Map((day?.groups ?? []).map(g => [g.groupId, g.slots ?? []]));
  let rowMinutes = null;
  const all = [];
  for (const g of groups ?? []) {
    if (!groupSlots.has(g.id) || groupSlots.get(g.id).length === 0) continue;
    rowMinutes = rowMinutes == null ? g.slotMinutes : gcd(rowMinutes, g.slotMinutes);
    for (const s of groupSlots.get(g.id)) all.push({start: new Date(s.startsAt).getTime(), end: new Date(s.endsAt).getTime()});
  }
  if (rowMinutes == null || all.length === 0) return {rows: [], blocks: new Map(), rowMinutes: 0};
  // X95 (R36 H1): the shared row builder (utils/bookingRows.js), the one /book's dayRows uses too.
  const built = slotRows(all, rowMinutes * 60000);
  const last = built[built.length - 1].end;
  const rows = built.map((r) => ({startsAt: new Date(r.start).toISOString(), time: r.start, end: r.end}));
  const startRow = (iso) => gridRowAt(rows, iso);
  const endRow = (iso) => gridRowAt(rows, iso, {end: true});
  const rowStart = (r) => (r < rows.length ? rows[r].time : last);

  // X76 (R26 L3): an open-play change leaves no slots (open play has no band), so its rows had no cells: nothing said
  // "Open play" there and the legend left it out. Inside the grid's rows, the change's uncovered time becomes "Open play"
  // cells (the walk-up state, the member grid's own), so the block, the legend and the words agree; a change that is
  // outside the rows (open play all day) stays in the strip and the day line only, as before.
  const openChanges = new Map((day?.groups ?? []).map(g => [g.groupId, (g.overrides ?? []).filter(o => o.kind === 'OpenPlay' && o.startsAt && o.endsAt)]));
  const blocks = new Map();
  for (const area of areas ?? []) {
    const slots = groupSlots.get(area.groupId) ?? [];
    const cells = [];
    for (const s of slots) {
      const cell = (s.areas ?? []).find(c => c.areaId === area.id);
      if (!cell) continue;
      const start = startRow(s.startsAt);
      const span = Math.max(1, endRow(s.endsAt) - start);
      const label = s.label;
      const prev = cells[cells.length - 1];
      const sameBooking = prev && cell.bookingId && prev.cell.bookingId === cell.bookingId && prev.row + prev.span === start;
      const sameState = prev && !cell.bookingId && !prev.cell.bookingId && prev.cell.status === cell.status && prev.cell.label === cell.label && prev.row + prev.span === start
          && (cell.status === 'frozen' || cell.status === 'closed' || cell.status === 'matchesOnly' || cell.status === 'open');
      if (sameBooking || sameState) {
        prev.span += span;
        prev.endsAt = s.endsAt;
      } else {
        cells.push({row: start, span, cell, slot: s, startsAt: s.startsAt, endsAt: s.endsAt, label});
      }
    }
    // Uncovered rows become Open play cells: a day change of open play (`changed`), else the rules' own open time
    // between the day's slots (`weekly`, X94 R35 H2).
    const fill = (from, to, extra) => {
      let r = from;
      while (r < to) {
        const covering = cells.find(c => c.row <= r && r < c.row + c.span);
        if (covering) { r = covering.row + covering.span; continue; }
        const next = cells.reduce((m, c) => (c.row > r && c.row < m ? c.row : m), to);
        const span = next - r;
        const startsAt = new Date(rowStart(r)).toISOString();
        const endsAt = new Date(rowStart(r + span)).toISOString();
        cells.push({row: r, span, cell: {areaId: area.id, status: 'open', label: 'Open play', bookingId: null, ...extra}, slot: null, startsAt, endsAt, label: 'Open play'});
        r += span;
      }
    };
    for (const o of openChanges.get(area.groupId) ?? []) fill(Math.max(0, startRow(o.startsAt)), Math.min(rows.length, endRow(o.endsAt)), {changed: true});
    // Only between the area's own group's first and last slot: outside them is another group's time on a shared axis.
    if (slots.length) {
      const first = slots.reduce((m, x) => (x.startsAt < m ? x.startsAt : m), slots[0].startsAt);
      const lastEnd = slots.reduce((m, x) => (x.endsAt > m ? x.endsAt : m), slots[0].endsAt);
      fill(startRow(first), endRow(lastEnd), {weekly: true});
    }
    cells.sort((a, b) => a.row - b.row);
    // X78 (R27 N1): the API's made-up slots (a booking's time in open play) put "Open play" cells on the other areas; those
    // and the fill's cells read as one block, as a run of any band does.
    for (let i = 1; i < cells.length; i++) {
      const prev = cells[i - 1];
      const cur = cells[i];
      if (prev.cell.status === 'open' && cur.cell.status === 'open' && !prev.cell.bookingId && !cur.cell.bookingId && prev.cell.label === cur.cell.label && prev.row + prev.span === cur.row) {
        prev.span += cur.span;
        prev.endsAt = cur.endsAt;
        if (cur.cell.changed) prev.cell = {...prev.cell, changed: true};
        if (prev.cell.weekly && !cur.cell.weekly) prev.cell = {...prev.cell, weekly: false};
        cells.splice(i, 1);
        i--;
      }
    }
    blocks.set(area.id, cells);
  }
  return {rows, blocks, rowMinutes};
}

function gcd(a, b) {
  return b === 0 ? a : gcd(b, a % b);
}

/** "Mon 20 Oct · 7–8pm" style grid labels come from the API; this one is for the row axis, in the club's zone. */
export function rowLabel(iso, timeZone) {
  try {
    return new Intl.DateTimeFormat('en-GB', {hour: 'numeric', minute: '2-digit', hour12: true, timeZone: timeZone || undefined}).format(new Date(iso)).replace(':00', '').replace(' ', '');
  } catch {
    return '';
  }
}

/** X143 (R70 L3): the "Right now" chips' clock: the grid's 24-hour one ("10:00", "9:30"), per Grant's 24h ruling (X91). */
export function chipClock(iso, timeZone) {
  const [h, m] = clockInZone(iso, timeZone).split(':').map(Number);
  return clock24(h * 60 + m);
}

/** X196: the one line under the Playing areas list's "Right now" heading (its tooltip). */
export const RIGHT_NOW_TOOLTIP = "What's happening on this playing area at the moment";

/**
 * X139 (R69 L-new-2), X196 (Grant 2026-10-27: "This lists Squash Court 1 as Open play which made me think it was Open
 * play always, but that actually means right now"): the Setup page's "Right now" chip per playing area, each with the
 * time the current state ends where the day's grid knows it ("Open play until 18:00", "Booked until 15:30", "Closed
 * until 14:00", "Free to book now"; 24h, chipClock). The end is the end of the run of back-to-back slots in the same
 * state (one booking by its id; anything else by status and label), not just the current slot's; a run reaching midnight
 * reads "for the rest of the day". `detail` is the booker, match or freeze reason, for the tooltip. From one day of
 * the availability grid and ONE instant `at` (ms), fetched with the grid and carried in the page payload, so the
 * server and the hydrating client compute the same words. Pure: {areaId: {text, cls, detail}}; {} for no grid.
 */
export function rightNowChips(a, at) {
  const out = {};
  if (!a || !Array.isArray(a.areas)) return out;
  const t = Number.isFinite(at) ? at : Date.now();
  const day = a.dayList?.[0];
  const tz = a.timeZone;
  const ms = (iso) => new Date(iso).getTime();
  for (const area of a.areas) {
    const slots = day?.groups?.find(g => g.groupId === area.groupId)?.slots ?? [];
    const i = slots.findIndex(s => ms(s.startsAt) <= t && ms(s.endsAt) > t);
    const cellOf = (s) => s?.areas?.find(c => c.areaId === area.id);
    const cell = i >= 0 ? cellOf(slots[i]) : null;
    if (!cell) {
      const next = slots.find(s => ms(s.startsAt) > t);
      out[area.id] = {text: next ? `Open play until ${chipClock(next.startsAt, tz)}` : 'Open play', cls: '', detail: ''};
      continue;
    }
    // one booking runs as long as its id does (the calendar's own merge); any other state as long as status and label hold
    const same = (c) => !!c && (cell.bookingId ? c.bookingId === cell.bookingId : !c.bookingId && c.status === cell.status && (c.label ?? '') === (cell.label ?? ''));
    let end = slots[i].endsAt;
    for (let j = i + 1; j < slots.length && ms(slots[j].startsAt) === ms(end) && same(cellOf(slots[j])); j++) end = slots[j].endsAt;
    const clock = chipClock(end, tz);
    const until = clock === '0:00' ? 'for the rest of the day' : `until ${clock}`;
    const detail = cell.label && cell.label !== 'Closed' && cell.label !== 'Other' ? cell.label : '';
    switch (cell.status) {
      case 'free': out[area.id] = {text: 'Free to book now', cls: 'tl-chip--ok', detail: ''}; break;
      case 'booked': case 'mine': out[area.id] = {text: `Booked ${until}`, cls: 'tl-chip--brand', detail: detail ? `Booked by ${detail}` : ''}; break;
      case 'match': out[area.id] = {text: `Match ${until}`, cls: 'tl-chip--brand', detail}; break;
      case 'frozen': out[area.id] = {text: `Frozen ${until}`, cls: 'tl-chip--warn', detail}; break;
      case 'closed': out[area.id] = {text: `Closed ${until}`, cls: 'tl-chip--warn', detail: ''}; break;
      case 'matchesOnly': out[area.id] = {text: `Matches only ${until}`, cls: '', detail: ''}; break;
      default: out[area.id] = {text: `Open play ${until}`, cls: '', detail: ''};
    }
  }
  return out;
}

/** "Mon 19 Oct" of an instant in the club's zone. */
export function dayWordIn(iso, timeZone) {
  try {
    return new Intl.DateTimeFormat('en-GB', {timeZone: timeZone || undefined, weekday: 'short', day: 'numeric', month: 'short'}).format(new Date(iso)).replace(',', '');
  } catch {
    return '';
  }
}

/**
 * One style for every admin span (TEST_T2 Low 5), the grid's: "Mon 19 Oct ·
 * 6pm–9pm"; across days "Mon 19 Oct 6pm – Tue 20 Oct 6pm". An end on the
 * stroke of midnight still belongs to the day it closes.
 */
export function spanLine(startsAt, endsAt, timeZone) {
  if (!startsAt || !endsAt) return '';
  const endDay = dayInZone(new Date(new Date(endsAt).getTime() - 1000).toISOString(), timeZone);
  if (dayInZone(startsAt, timeZone) === endDay) return `${dayWordIn(startsAt, timeZone)} · ${rowLabel(startsAt, timeZone)}–${rowLabel(endsAt, timeZone)}`;
  return `${dayWordIn(startsAt, timeZone)} ${rowLabel(startsAt, timeZone)} – ${dayWordIn(endsAt, timeZone)} ${rowLabel(endsAt, timeZone)}`;
}

/** "Mon 19 Oct, 7pm" for one moment. */
export function momentLine(iso, timeZone) {
  if (!iso) return '';
  return `${dayWordIn(iso, timeZone)}, ${rowLabel(iso, timeZone)}`;
}

/**
 * A freeze in one line, the frozen block's tooltip on the calendar: "Court 1, Court 2 · Tue 20 Oct 8:00 – Wed 21 Oct
 * 8:00 · Maintenance". X91: in the grid's 24-hour clock (gridSpanLine), as the grid around it.
 */
export function freezeLine(closure, timeZone) {
  if (!closure) return '';
  const names = (closure.playingAreaNames ?? []).join(', ');
  const reason = FREEZE_REASONS.find(r => r.value === closure.reason)?.title ?? closure.reason ?? '';
  return [names, gridSpanLine(closure.startsAt, closure.endsAt, timeZone), reason].filter(Boolean).join(' · ');
}

/** X91: spanLine in the grid's 24-hour clock: "Mon 19 Oct · 18:00–21:00"; across days "Tue 20 Oct 8:00 – Wed 21 Oct 8:00". */
export function gridSpanLine(startsAt, endsAt, timeZone) {
  if (!startsAt || !endsAt) return '';
  const endDay = dayInZone(new Date(new Date(endsAt).getTime() - 1000).toISOString(), timeZone);
  if (dayInZone(startsAt, timeZone) === endDay) return `${dayWordIn(startsAt, timeZone)} · ${periodInZone(startsAt, endsAt, timeZone)}`;
  return `${dayWordIn(startsAt, timeZone)} ${gridTime(startsAt, timeZone)} – ${dayWordIn(endsAt, timeZone)} ${gridTime(endsAt, timeZone)}`;
}

/** X94 (R35 L2): momentLine on the grid's 24-hour clock: "Mon 19 Oct, 19:00". */
export function gridMomentLine(iso, timeZone) {
  if (!iso) return '';
  return `${dayWordIn(iso, timeZone)}, ${gridTime(iso, timeZone)}`;
}

/**
 * X94 (R35 L2): an API booking line ("Fri 23 Oct · 11am–12pm · Court 2") with its times on the grid's 24-hour clock
 * ("Fri 23 Oct · 11:00–12:00 · Court 2"), from the instants it names. A line with no time part comes back as it is.
 */
export function line24(line, startsAt, endsAt, timeZone) {
  const text = String(line ?? '');
  if (!text || !startsAt || !endsAt) return text;
  const parts = text.split(' · ');
  const i = parts.findIndex((p) => /\d(am|pm)\b|^\d{1,2}(:\d{2})?\s*[–-]/.test(p));
  if (i < 0) return text;
  parts[i] = periodInZone(startsAt, endsAt, timeZone);
  return parts.join(' · ');
}

export const BOOKING_STATUS_WORDS = {
  Queued: 'Booking opens later',
  Held: 'Held',
  PendingPayment: 'Waiting for payment',
  Confirmed: 'Confirmed',
  Cancelled: 'Cancelled',
  Released: 'Released',
  NoShow: 'No-show',
};

/** X232 (R96 L3): the club's own booking is a "Club block" here too, the All bookings list's word. */
export const BOOKING_KIND_WORDS = {Play: 'Booking', Match: 'Match', MeetHold: 'Meet', Admin: 'Club block', Visitor: 'Visitor'};

/**
 * The admin calendar block's main word: the API's label, else the state's word. X95 (R36 L6): a freeze with no reason
 * (Other) comes as "Closed", the same word as the Closed hours; a freeze is drawn "Frozen" (its look and the legend's
 * Frozen entry), and a reasoned one keeps its reason ("Maintenance").
 */
export function calendarBlockWord(cell) {
  const c = cell ?? {};
  if (c.status === 'frozen') return c.label && c.label !== 'Closed' && c.label !== 'Other' ? c.label : 'Frozen';
  return c.label || (c.status === 'match' ? 'Match' : c.status === 'closed' ? 'Closed' : c.status === 'matchesOnly' ? 'Matches only' : c.status === 'open' ? 'Open play' : '');
}

/** The one-line "who" for a calendar block: the API's label plus guests and chips. */
export function blockTitle(cell) {
  return cell?.label || (cell?.status === 'match' ? 'Match' : 'Booked');
}

export function shiftDate(yyyyMmDd, days) {
  const [y, m, d] = String(yyyyMmDd).split('-').map(n => parseInt(n, 10));
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
}

/**
 * TapDatePicker's model is an instant that `new Date()` reads back as a day in
 * the BROWSER's zone, so a club-zone day goes in as the browser's local
 * midnight of that calendar day (never the club's midnight as an instant: an
 * admin in New York would see the day before). Both ways share the calendar
 * day; only the time is put into the club's zone, by whoever builds the body.
 */
export function pickerIsoForDay(yyyyMmDd) {
  const [y, m, d] = String(yyyyMmDd ?? '').split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).toISOString();
}

/** The calendar day a TapDatePicker value names, in the browser's zone (the inverse of pickerIsoForDay). */
export function dayFromPickerIso(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** "2026-10-19" of an instant in the club's zone. */
export function dayInZone(iso, timeZone) {
  try {
    return new Intl.DateTimeFormat('en-CA', {timeZone: timeZone || undefined, year: 'numeric', month: '2-digit', day: '2-digit'}).format(new Date(iso));
  } catch {
    return String(iso).slice(0, 10);
  }
}

/** "HH:mm" of an instant in the club's zone. */
export function clockInZone(iso, timeZone) {
  try {
    return new Intl.DateTimeFormat('en-GB', {timeZone: timeZone || undefined, hour: '2-digit', minute: '2-digit', hourCycle: 'h23'}).format(new Date(iso));
  } catch {
    return '00:00';
  }
}

/**
 * X91 (Grant 2026-10-21: the times on the left "can be normal cells … and have the full time period in 24hr format e.g.
 * 8:00-8:30, 14:00-15:00"): a clock in 24-hour time with no leading zero ("8:00", "14:30", "0:00"); an END on the
 * stroke of midnight (after a start) reads "24:00", as the day-change dialog's To list does.
 */
export function clock24(minutes, {end = false} = {}) {
  const n = Math.round(Number(minutes) || 0);
  const m = ((n % 1440) + 1440) % 1440;
  if (end && m === 0 && n > 0) return '24:00';
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

/** X91: a slot row's whole period, "8:00–8:30", "14:00–15:00", "23:30–24:00" (minutes of the day; an en dash). */
export function periodWord(startMinutes, endMinutes) {
  return `${clock24(startMinutes)}\u2013${clock24(endMinutes, {end: true})}`;
}

/**
 * X91 (Grant 2026-10-21: the times in the cells "should always be in 24hr clock format for consistency"): THE grid
 * clock. Every time inside the booking grids (cells, blocks, tooltips, ghosts, the time column) goes through clock24 /
 * periodWord, via these: an "HH:mm" label ("18:30" → "18:30", "08:00" → "8:00", "01:00 (+00:00)" keeps its tail),
 * and an instant on the club's clock.
 */
export function clockWord(label, {end = false} = {}) {
  const text = String(label ?? '').trim();
  const m = text.match(/^(\d{1,2}):(\d{2})(.*)$/);
  if (!m) return text;
  const rest = m[3] ? ' ' + m[3].trim() : '';
  return clock24(parseInt(m[1], 10) * 60 + parseInt(m[2], 10), {end}) + rest;
}

/** X91: an instant on the club's clock in the grid's 24-hour style ("18:30"); an end at midnight is "24:00". */
export function gridTime(iso, timeZone, {end = false} = {}) {
  if (!iso) return '';
  const hhmm = clockInZone(iso, timeZone);
  return clockWord(end && hhmm === '00:00' ? '24:00' : hhmm, {end});
}

/** X91: the period of two instants on the club's clock (the admin calendar's time cells, ghosts and tooltips). */
export function periodInZone(startIso, endIso, timeZone) {
  const mins = (iso) => { const [h, m] = clockInZone(iso, timeZone).split(':').map(x => parseInt(x, 10)); return (h || 0) * 60 + (m || 0); };
  const from = mins(startIso);
  let to = mins(endIso);
  if (to <= from) to += 1440;
  return periodWord(from, to);
}

/** X57: whether an instant sits on the hour in the club's zone (the admin calendar labels those rows only, like /book). */
export function rowIsHour(iso, timeZone) {
  return clockInZone(iso, timeZone).endsWith(':00');
}

/** A club-zone day + "HH:mm" as a UTC instant (the freeze dialog's body). */
export function instantInZone(yyyyMmDd, hhmm, timeZone) {
  if (!yyyyMmDd) return null;
  const [y, m, d] = String(yyyyMmDd).split('-').map(n => parseInt(n, 10));
  const [hh, mm] = String(hhmm || '00:00').split(':').map(n => parseInt(n, 10));
  if (!y || !m || !d) return null;
  // Find the UTC instant whose wall clock in the zone is the asked-for one:
  // start from the UTC guess and correct by the zone's offset at that instant.
  const guess = Date.UTC(y, m - 1, d, hh || 0, mm || 0);
  const wall = (t) => {
    const p = new Intl.DateTimeFormat('en-GB', {timeZone: timeZone || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'}).formatToParts(new Date(t));
    const g = (k) => parseInt(p.find(x => x.type === k)?.value ?? '0', 10);
    return Date.UTC(g('year'), g('month') - 1, g('day'), g('hour') % 24, g('minute'));
  };
  let t = guess - (wall(guess) - guess);
  if (wall(t) !== guess) t = guess - (wall(t) - t);
  return new Date(t).toISOString();
}

export function todayIn(timeZone) {
  try {
    return new Intl.DateTimeFormat('en-CA', {timeZone: timeZone || undefined, year: 'numeric', month: '2-digit', day: '2-digit'}).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

export function longDate(yyyyMmDd) {
  const [y, m, d] = String(yyyyMmDd).split('-').map(n => parseInt(n, 10));
  if (!y) return '';
  return new Intl.DateTimeFormat('en-GB', {weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC'}).format(new Date(Date.UTC(y, m - 1, d)));
}

// ── ?setup=1 on the Bookings summary (TEST_T2 M10, R4 NM3, R6 NM3) ──

/** What ?setup=1 asks of the summary: nothing, just drop the query (already on, or the feature is off), or start the switch-on. */
/**
 * R8 NM3: whether ?setup=1 is on the route. app/router.options.ts parses the query with the Crud parseQuery
 * (parseNumbers), so a direct load gives the number 1 while an in-app push of {setup: '1'} gives the string.
 */
export function setupRequested(query) {
    return queryFlag(query, 'setup');
}

export function setupQueryAction({requested, on, feature, ready = true}) {
    if (!requested) return 'none';
    if (on || !feature) return 'tidy';
    // X133: nothing to switch on yet (no set of booking rules with hours and a bookable area): the page already shows
    // the step and its reason, so the query is only dropped.
    if (!ready) return 'tidy';
    return 'start';
}

// ── The switch-on time-zone dialog (B15; X60, R24 L-c) ──

/** The zone offered when nothing places the club and the browser's zone isn't in the list. */
export const FALLBACK_ZONE = 'Europe/London';

/** The option rows the zone pickers show: "UK time (+01:00)" over the IANA id. */
export function zoneOptions(zones) {
  return (zones ?? []).map(z => ({value: z.id, title: `${z.label} (${z.offset})`, subtitle: z.id}));
}

/**
 * What the "Which time zone?" dialog says and offers, from the settings the API gave:
 * - a club whose country keeps several clocks (timeZoneNeedsChoice with a suggestedTimeZone, the country's
 *   main one): the old copy, "Your country keeps several clocks, so choose the one {club} is on.", the
 *   suggestion chosen, the plain list (unchanged behaviour);
 * - a club nothing places (no country, no map point: no suggestion at all): "Pick your club's time zone.",
 *   the browser's zone when the list has it, else Europe/London, chosen and lifted to the top as "Suggested",
 *   and the list searchable (it is every zone in the world).
 * `chosen` is null only when the list is empty (the zones couldn't be loaded): the admin picks nothing and the
 * API chooses from what it knows.
 */
export function zoneChoice({settings, zones, browserZone, clubName}) {
  const options = zoneOptions(zones);
  const suggested = settings?.timeZone || settings?.suggestedTimeZone || null;
  if (suggested) {
    return {
      placed: true,
      copy: settings?.timeZoneNeedsChoice ? `Your country keeps several clocks, so choose the one ${clubName || 'the club'} is on.` : '',
      items: options,
      chosen: suggested,
      search: false,
    };
  }
  const has = (id) => !!id && options.some(o => o.value === id);
  const chosen = has(browserZone) ? browserZone : has(FALLBACK_ZONE) ? FALLBACK_ZONE : null;
  const top = chosen ? options.find(o => o.value === chosen) : null;
  const items = top ? [{...top, subtitle: `${top.subtitle} · Suggested`}, ...options.filter(o => o.value !== chosen)] : options;
  return {placed: false, copy: "Pick your club's time zone.", items, chosen, search: true};
}

/** The searchable list matches the label ("UK time") OR the zone id ("Europe/London"), so typing a city always finds it. */
export function zoneMatches(item, query) {
  const q = String(query ?? '').trim().toLowerCase();
  if (!q) return true;
  const hay = [item?.title, item?.subtitle, item?.value].filter(Boolean).map(s => String(s).toLowerCase().replace(/_/g, ' '));
  return hay.some(s => s.includes(q.replace(/_/g, ' ')));
}

// Moved to utils/queryFlags.js (R10 NH1), so other query-flag dialogs share it; re-exported for the bookings pages.
export {whenHydrated} from './queryFlags.js';

// ── The Bookings summary page (X41 redesign, X43 refinement) ──

/** "same day only", "7 days ahead". */
export function aheadWord(daysAhead) {
  return daysAhead === 0 ? 'same day only' : `${daysAhead} day${daysAhead === 1 ? '' : 's'} ahead`;
}

/** The No membership row's allowance as words: "2 hours max · 7 days ahead · 2 at once". */
function allowanceParts(a) {
  const parts = [durationWord(a.maxDurationMinutes, a.allDay) + ' max', aheadWord(a.daysAhead)];
  if (a.maxActiveBookings != null) parts.push(`${a.maxActiveBookings} at once`);
  return parts;
}

/**
 * The No membership row of a group for the list's column: its price ("£6 a
 * slot" / "Free"), whether it is priced, the allowance line, or cantBook.
 * null when the group has no such row.
 */
export function baseAllowance(group, money) {
  const base = (group?.whoCanBook ?? []).find(r => r.audience === AUDIENCE.noMembership)?.effective ?? null;
  if (!base) return null;
  if (base.canBook === false) return {cantBook: true, price: "Can't book", priced: false, line: ''};
  return {cantBook: false, price: base.priceMinor ? `${money(base.priceMinor)} a slot` : 'Free', priced: !!base.priceMinor, line: allowanceParts(base).join(' · ')};
}

/**
 * A group's rules as a few readable chips, each with a key so the page can
 * tell what every group shares from what differs (sharedRules): 'slot',
 * 'base' (the No membership row, named as the who-can-book table names it,
 * price included: "No membership · £6 a slot · 2 hours max · 7 days ahead ·
 * 2 at once"), 'type:<label>' only where a membership type differs from No
 * membership, 'guests', 'visitors', 'cutoff'. `money(minor)` formats a price.
 * tone: '' | 'muted' | 'brand' | 'ok' | 'warn' (the tl-chip tints).
 */
export function groupRuleChips(group, money) {
  const rows = group?.whoCanBook ?? [];
  const find = (audience) => rows.find(r => r.audience === audience);
  const base = find(AUDIENCE.noMembership)?.effective ?? null;
  const chips = [{key: 'slot', text: `${durationWord(group?.slotMinutes ?? 60)} slots`, tone: 'muted', icon: 'mdi-timer-outline'}];
  if (base) {
    if (base.canBook === false) chips.push({key: 'base', text: "No membership · can't book", tone: 'warn'});
    else chips.push({key: 'base', text: ['No membership', base.priceMinor ? `${money(base.priceMinor)} a slot` : 'free', ...allowanceParts(base)].join(' · '), tone: ''});
  }
  for (const r of rows.filter(r => r.audience === AUDIENCE.membershipType)) {
    const e = r.effective;
    if (!e || !base) continue;
    if (e.canBook === false) {
      if (base.canBook !== false) chips.push({key: `type:${r.label}`, text: `${r.label} can't book`, tone: 'muted'});
      continue;
    }
    const diffs = [];
    if (base.canBook === false) diffs.push('can book');
    if (e.maxDurationMinutes !== base.maxDurationMinutes || !!e.allDay !== !!base.allDay) diffs.push(durationWord(e.maxDurationMinutes, e.allDay) + ' max');
    if (e.daysAhead !== base.daysAhead) diffs.push(aheadWord(e.daysAhead));
    if ((e.maxActiveBookings ?? null) !== (base.maxActiveBookings ?? null)) diffs.push(e.maxActiveBookings == null ? 'no limit at once' : `${e.maxActiveBookings} at once`);
    if ((e.priceMinor ?? 0) !== (base.priceMinor ?? 0)) diffs.push(e.priceMinor ? `${money(e.priceMinor)} a slot` : 'free');
    if (diffs.length) chips.push({key: `type:${r.label}`, text: `${r.label} · ${diffs.join(' · ')}`, tone: ''});
  }
  const guest = find(AUDIENCE.guest)?.effective;
  chips.push(group?.guestsAllowed
    ? {key: 'guests', text: guest?.priceMinor ? `Guests ${money(guest.priceMinor)}` : 'Guests free', tone: ''}
    : {key: 'guests', text: 'No guests', tone: 'muted'});
  const visitor = find(AUDIENCE.visitor)?.effective;
  chips.push(group?.visitorsAllowed
    ? {key: 'visitors', text: visitor?.priceMinor ? `Visitors ${money(visitor.priceMinor)}` : 'Visitors free', tone: ''}
    : {key: 'visitors', text: 'No visitors', tone: 'muted'});
  chips.push({key: 'cutoff', text: group?.cancelCutoffMinutes ? `Cancel up to ${cutoffWord(group.cancelCutoffMinutes)}` : 'Cancel any time', tone: 'muted'});
  return chips;
}

/**
 * What most groups share and what each one differs in, so the list says the
 * shared rules once ("In every group: …", or "In most groups: …" with "2 of 3"
 * on a chip not every group has) and a row shows only what is its own. X46
 * (R21 N6): a rule's value is shared when a strict majority of the groups, at
 * least two, have it; the rows that don't show theirs. The slot length and the
 * No membership row have columns of their own, so they are left out. With one
 * group nothing is "shared": its chips are all its own. Returns {shared: chips
 * (+ count, of, all), differences: Map(groupId → chips), allShared}.
 */
export function sharedRules(groups, money) {
  const list = groups ?? [];
  const per = list.map(g => ({id: g.id, chips: groupRuleChips(g, money).filter(c => c.key !== 'slot' && c.key !== 'base')}));
  const counts = new Map();
  for (const p of per) for (const c of p.chips) {
    const m = counts.get(c.key) ?? new Map();
    m.set(c.text, (m.get(c.text) ?? 0) + 1);
    counts.set(c.key, m);
  }
  const majority = new Map();
  if (list.length > 1) {
    for (const [key, m] of counts) {
      const [text, n] = [...m].sort((a, b) => b[1] - a[1])[0];
      if (n >= 2 && n * 2 > list.length) majority.set(key, {text, n});
    }
  }
  const isShared = (c) => majority.get(c.key)?.text === c.text;
  const shared = [];
  const seen = new Set();
  for (const p of per) for (const c of p.chips) {
    if (!isShared(c) || seen.has(c.key)) continue;
    seen.add(c.key);
    const {n} = majority.get(c.key);
    shared.push({...c, count: n, of: list.length, all: n === list.length});
  }
  return {
    shared,
    differences: new Map(per.map(p => [p.id, p.chips.filter(c => !isShared(c))])),
    allShared: shared.every(c => c.all),
  };
}

/**
 * The playing areas of a group as words: the bookable ones' names, the
 * walk-up ones apart (they get the dashed chip), and the summary
 * ("all bookable", "1 of 2 bookable", "none bookable").
 */
export function areasWords(group) {
  const areas = group?.playingAreas ?? [];
  const bookable = areas.filter(a => a.canBeBooked);
  const walkUp = areas.filter(a => !a.canBeBooked);
  const summary = !areas.length ? '' : bookable.length === areas.length ? 'all bookable' : bookable.length === 0 ? 'none bookable' : `${bookable.length} of ${areas.length} bookable`;
  return {bookable: bookable.map(a => a.name), walkUp: walkUp.map(a => a.name), count: areas.length, summary};
}

/**
 * X83: the playing areas of a set of booking rules on one quiet line:
 * "4 playing areas" and which ("Court 1, Court 2, Court 3 and 1 more" past
 * three; the walk-up ones counted: "2 of 4 can be booked"). `all` carries
 * every name for the tooltip when the line is cut.
 */
export function areasLine(group, max = 3) {
  const areas = group?.playingAreas ?? [];
  const names = areas.map(a => a.name);
  // X232 (R96 L5): an empty set reads "No playing areas yet", as the set page's line does, never "0 playing areas".
  if (!areas.length) return {count: 'No playing areas yet', names: '', all: '', cut: false, bookable: null};
  const count = `${areas.length} playing area${areas.length === 1 ? '' : 's'}`;
  const shown = names.slice(0, max);
  const rest = names.length - shown.length;
  const line = rest > 0 ? `${shown.join(', ')} and ${rest} more` : shown.length > 1 ? `${shown.slice(0, -1).join(', ')} and ${shown.at(-1)}` : shown[0];
  const canBook = areas.filter(a => a.canBeBooked).length;
  const bookable = canBook === areas.length ? null : canBook === 0 ? 'none can be booked' : `${canBook} of ${areas.length} can be booked`;
  return {count, names: line, all: names.join(', '), cut: rest > 0, bookable};
}

/**
 * X84 (R30 L2): the remove confirm and its toast. A set with playing areas hands them to another set; an empty one
 * says nothing about playing areas (there are none to move).
 */
export function removeConfirmMessage(group) {
  const name = group?.name || 'these booking rules';
  if (!(group?.playingAreas ?? []).length) return `${name} has no playing areas, so nothing else changes.`;
  return `The playing areas under ${name} move to another set of booking rules and take its hours, rules and prices from the next booking.`;
}
export function removedToast(group) {
  const name = group?.name || 'The booking rules';
  if (!(group?.playingAreas ?? []).length) return `${name} removed.`;
  return `${name} removed; their playing areas moved to another set of booking rules.`;
}

/**
 * X83: the Bookings page's tabs (?tab=). X133 (Grant 2026-10-26: "this page defaults to the 3rd tab along which seems
 * weird… a lot of the tabs are irrelevant if bookings are disabled… Booking rules would have been the first tab"):
 * which tabs the page has depends on the club's bookings SCENARIO (bookingsScenario), Settings is only there while
 * bookings are on and always last, and a club that never set bookings up gets no tabs at all (a focused setup view).
 */
export const BOOKINGS_TABS = Object.freeze([
  {value: 'calendar', title: 'Calendar'},
  // X93: every booking, paged, filtered and downloadable (components/admin/bookings/AllBookingsList.vue).
  {value: 'bookings', title: 'All bookings'},
  {value: 'rules', title: 'Booking rules'},
  {value: 'website', title: 'Widgets'}, // X146: the value stays so ?tab=website links still work
  {value: 'settings', title: 'Settings'},
]);

/** X133: the tabs each scenario shows, in order. */
export const BOOKINGS_SCENARIO_TABS = Object.freeze({
  // Bookings on: the calendar leads (Booking rules first only while a set needs attention: pickBookingsTab).
  on: ['calendar', 'bookings', 'rules', 'website', 'settings'],
  // Switched off after being used: the rules, the history (with its download), the website; no calendar, no Settings.
  offUsed: ['rules', 'bookings', 'website'],
  // Booking rules exist but bookings were never on: the rules first, then the website.
  offRules: ['rules', 'website'],
  // Never set up (off, no rules, never a booking): no tabs, the setup view.
  new: [],
  // Tap Leagues has not enabled bookings for the club.
  disabled: [],
});

/**
 * X133: which Bookings page a club gets. `hasBookings` = the club has EVER had a booking (the clubBookings count
 * with no filters); `rules` = how many sets of booking rules it has (or the array).
 */
export function bookingsScenario({feature = true, on = false, rules = 0, hasBookings = false} = {}) {
  if (!feature) return 'disabled';
  if (on) return 'on';
  if (hasBookings) return 'offUsed';
  const count = Array.isArray(rules) ? rules.length : (rules ?? 0);
  if (count > 0) return 'offRules';
  return 'new';
}

export function bookingsTabs({on, feature = true, rules = 0, hasBookings = false} = {}) {
  const scenario = bookingsScenario({feature, on, rules, hasBookings});
  const values = BOOKINGS_SCENARIO_TABS[scenario] ?? [];
  return values.map(v => BOOKINGS_TABS.find(t => t.value === v)).filter(Boolean);
}

/**
 * The tab the query names when the page has it, else the scenario's first: Calendar while bookings are on, except
 * that Booking rules opens first while a set needs attention (`attention` > 0: no hours, nothing bookable, no
 * areas); off, the first tab (Booking rules). A ?tab= the scenario does not show falls back the same way. Null
 * when the page has no tabs.
 */
export function pickBookingsTab(tabs, query, {attention = 0} = {}) {
  const wanted = query == null ? null : String(query);
  const has = (v) => tabs.some(t => t.value === v);
  if (wanted && has(wanted)) return wanted;
  if (has('calendar')) return attention > 0 && has('rules') ? 'rules' : 'calendar';
  return tabs[0]?.value ?? null;
}

/** X133: a set of booking rules that can take bookings: it has hours and at least one playing area that can be booked. */
export function groupReady(group) {
  return !!(group?.windows ?? []).length && (group?.playingAreas ?? []).some(a => a.canBeBooked);
}

/**
 * X136 (R65 H1): the one thing to do before bookings can be switched on, with the set of booking rules it is done
 * on, so the blocker line is an action in place and never a dead end: {action, group, line}, or null when a set is
 * ready. create = New booking rules; paint = the hours painter on a set with no hours (one with areas first);
 * move = a set has hours but no playing areas (Move a playing area here, or Add one when the club has none);
 * bookable = a set has hours and areas but every one is walk-up (the Can be booked switches).
 */
export function switchOnFix(groups, settings = null) {
  const list = groups ?? [];
  if (list.some(groupReady)) {
    // X137 (R66 M2): prices are drafted before payments; the switch-on needs the payment account first (the API
    // refuses the same way). `settings` is the bookings settings (paymentsReady); without it nothing is assumed.
    const priced = settings && settings.paymentsReady === false ? list.filter(groupPriced) : [];
    if (priced.length) return {action: 'payments', group: priced[0], line: pricedLine(priced)};
    return null;
  }
  if (!list.length) return {action: 'create', group: null, line: 'Create booking rules first.'};
  const withHours = list.filter(g => (g.windows ?? []).length);
  if (!withHours.length) return {action: 'paint', group: list.find(g => (g.playingAreas ?? []).length) ?? list[0], line: 'Paint booking hours on your booking rules first.'};
  const withAreas = withHours.filter(g => (g.playingAreas ?? []).length);
  if (!withAreas.length) return {action: 'move', group: withHours[0], line: 'Move a playing area under your booking rules first.'};
  return {action: 'bookable', group: withAreas[0], line: 'Choose a playing area that can be booked first.'};
}

/**
 * X133: why bookings can't be switched on yet, in one line (the disabled Switch bookings on says it); null when a
 * set of booking rules is ready. The line names the one thing to do next (switchOnFix says where it is done).
 */
export function switchOnBlocker(groups, settings = null) {
  return switchOnFix(groups, settings)?.line ?? null;
}

/** X137: a set with a price on any of its who-can-book rows (the row's own, not an inherited one). */
export function groupPriced(group) {
  return (group?.whoCanBook ?? []).some(r => (r.priceMinor ?? 0) > 0);
}

const pricedLine = (groups) => {
  const names = groups.map(g => g.name);
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
  return `Set up payments before switching bookings on: ${list} ${names.length === 1 ? 'has' : 'have'} prices.`;
};

/**
 * Why a set of booking rules needs attention (no playing areas, no hours,
 * nothing bookable), with one sentence and the one action that fixes it; null
 * when it's fine. Those sort first in the list.
 * X230 (R95-M1): a set with no playing areas only needs attention when the club HAS playing areas (one could be
 * moved under it). A club with none (every new club has the default set, "Booking rules"; "All playing areas" before 2026-11-04) is not nagged: the
 * Bookings page opens on the calendar's "No playing areas yet". `clubHasAreas` defaults to true (old callers).
 */
export function groupAttention(group, clubId, {clubHasAreas = true} = {}) {
  const areas = group?.playingAreas ?? [];
  const edit = {name: 'ClubBookingGroup', params: {clubId, id: group?.id}};
  if (!areas.length && !clubHasAreas) return null;
  if (!areas.length) return {text: 'No playing areas', help: 'Move a playing area under these rules or remove them.', route: edit, link: 'Open the rules'};
  // X96 (act in place): the hours painter and the Can be booked switches open in a dialog on the Rules tab (`action`);
  // `route` stays for a caller with no dialog. "No playing areas" opens the set, where areas are moved under it.
  if (!(group?.windows ?? []).length) return {text: 'No booking hours yet', help: 'Nothing can be booked here until you paint hours.', route: {...edit, query: {section: 'hours'}}, link: 'Paint the hours', action: 'paint'};
  if (!areas.some(a => a.canBeBooked)) return {text: 'Nothing can be booked', help: 'Every area here is walk-up. Choose which can be booked to let players book them.', route: {name: 'ClubPlayingAreas', params: {clubId}}, link: 'Choose which can be booked', action: 'bookable'};
  return null;
}

/** X230: whether the club has any playing area: under any set of booking rules, or in the club's own list. */
export function clubHasPlayingAreas(groups, clubAreas) {
  return (groups ?? []).some(g => (g?.playingAreas ?? []).length > 0) || (clubAreas ?? []).length > 0;
}

/** Attention groups first, then the club's order. */
export function sortGroups(groups, clubId, {clubHasAreas = true} = {}) {
  const opts = {clubHasAreas};
  return [...(groups ?? [])].sort((a, b) => (groupAttention(b, clubId, opts) ? 1 : 0) - (groupAttention(a, clubId, opts) ? 1 : 0) || (a.sort ?? 0) - (b.sort ?? 0));
}

/**
 * One shared axis for every week bar in the list, trimmed to the earliest
 * start and latest end across all the bands (rounded out to the hour); a band
 * past midnight carries the end past 1440. {start, end} in minutes.
 */
export function weekAxis(windows) {
  let start = 1440;
  let end = 0;
  for (const w of windows ?? []) {
    const s = minutesOf(w.startLocal);
    let e = minutesOf(w.endLocal);
    if (e <= s) e += 1440;
    start = Math.min(start, s);
    end = Math.max(end, e);
  }
  if (end <= start) return {start: 0, end: 1440};
  return {start: Math.floor(start / 60) * 60, end: Math.ceil(end / 60) * 60};
}

/**
 * The hour marks on an axis: its two ends always (X46, R21: so a bar is seen to
 * start at 8am and end at 2am), then every 3 hours up to a 12-hour span, else
 * every 6, a regular mark within 15% of an end giving way to it (an end label is
 * anchored inside the track, so "12am" at 89% ran into "2am"); {at: %, label}.
 */
export function axisTicks(axis) {
  const span = axis.end - axis.start;
  const step = (span <= 12 * 60 ? 3 : 6) * 60;
  const ticks = [];
  for (let m = Math.ceil(axis.start / step) * step; m <= axis.end; m += step) {
    ticks.push({at: (m - axis.start) / span * 100, label: timeWord(m % 1440)});
  }
  const ends = [{at: 0, label: timeWord(axis.start % 1440)}, {at: 100, label: timeWord(axis.end % 1440)}];
  return [ends[0], ...ticks.filter(t => ends.every(e => Math.abs(t.at - e.at) >= 15)), ends[1]];
}

/**
 * The week in miniature for a group row: one bar per day (Mon first) with
 * each band a block placed by percentage of the AXIS (weekAxis of the whole
 * list, so every group's bar compares; the group's own axis if none is given).
 * Adjacent bands of one kind merge into one block; a band past midnight stays
 * on its start day, running past the day's end.
 */
export function weekBars(windows, axis = null) {
  const ax = axis ?? weekAxis(windows);
  const span = Math.max(1, ax.end - ax.start);
  const perDay = new Map(DAYS.map(d => [d.n, []]));
  for (const w of windows ?? []) {
    const s = minutesOf(w.startLocal);
    let e = minutesOf(w.endLocal);
    if (e <= s) e += 1440;
    const days = w.days?.length ? w.days : daysFromMask(w.daysMask ?? 0);
    for (const day of days) perDay.get(day)?.push({start: s, end: e, kind: w.kind});
  }
  const pct = (m) => Math.max(0, Math.min(100, (m - ax.start) / span * 100));
  return DAYS.map(d => {
    const merged = [];
    for (const b of perDay.get(d.n).sort((a, b) => a.start - b.start || a.end - b.end)) {
      const prev = merged[merged.length - 1];
      if (prev && prev.kind === b.kind && b.start <= prev.end) prev.end = Math.max(prev.end, b.end);
      else merged.push({...b});
    }
    return {
      day: d.n,
      letter: d.letter,
      two: d.two,
      short: d.short,
      blocks: merged.map(b => ({left: pct(b.start), width: pct(b.end) - pct(b.start), kind: b.kind, title: `${d.short} ${timeWord(b.start)}–${timeWord(b.end)} · ${kindLabel(b.kind)}`})),
    };
  });
}

// X99: moved to utils/pillTabs (the shared TlPillTabs); kept exported here for the Bookings callers and tests.
export {tabsOverflow} from './pillTabs.js';

/**
 * X85 (R31 N4): the Switch bookings off dialog's opening sentence, counted right for one booking ("Keep it (it still
 * happens…)") and for many. Queued or held match bookings have their own note in the dialog, so they're not repeated.
 * preview: {futureBookings, paidBookings, refundTotalMinor, queuedOrHeld}; money formats minor units.
 */
export function bookingsOffMessage(preview, money) {
  const n = preview?.futureBookings ?? 0;
  const paid = preview?.paidBookings ?? 0;
  if (n === 0) return (preview?.queuedOrHeld ?? 0) > 0 ? 'Nothing else is booked ahead.' : 'Nothing is booked ahead. Bookings can be switched off now.';
  const refund = paid > 0 ? ` (${money(preview.refundTotalMinor)} to refund if cancelled)` : '';
  if (n === 1) {
    return `1 booking is in the future${paid > 0 ? `, and it is paid${refund}` : ''}. `
      + `Keep it (it still happens; nothing new can be booked) or ${paid > 0 ? 'cancel and refund it' : 'cancel it'}.`;
  }
  const paidPart = paid === 0 ? '' : paid === n ? `, all of them paid${refund}` : `, ${paid} of them paid${refund}`;
  return `${n} bookings are in the future${paidPart}. `
    + `Keep them (they still happen; nothing new can be booked) or ${paid > 0 ? 'cancel and refund them all' : 'cancel them all'}.`;
}

/**
 * X87 (R32 F2): the Switch bookings off dialog's two option titles, in the opening line's words: "Keep the booking" for
 * one; "Cancel it" / "Cancel them all" when nothing is paid, "Cancel and refund it" / "Cancel and refund them all" when
 * something is. preview: {futureBookings, paidBookings} → {keep, cancel}.
 */
export function bookingsOffChoiceTitles(preview) {
  const one = (preview?.futureBookings ?? 0) === 1;
  const paid = (preview?.paidBookings ?? 0) > 0;
  return {
    keep: one ? 'Keep the booking' : 'Keep existing bookings',
    cancel: `Cancel${paid ? ' and refund' : ''} ${one ? 'it' : 'them all'}`,
  };
}

/**
 * X234 (Grant 2026-10-29): the hours change preview's choice for the club's own blocks it would cancel (the API's
 * clubBlocks count, the same whichever way the choice goes): keep them as they are (an admin override, the Admin chip in
 * All bookings) or cancel them. Members' bookings are not part of it. Keep is the default, as Switch bookings off.
 * null when the change touches no club block.
 */
export const CLUB_BLOCKS_DEFAULT = 'keep';
export function clubBlocksChoiceWords(preview) {
  const n = preview?.clubBlocks ?? 0;
  if (!n) return null;
  const one = n === 1;
  return {
    label: 'Club blocks',
    lead: one ? 'This change affects 1 of the club\'s own blocks.' : `This change affects ${n} of the club's own blocks.`,
    keep: {
      title: one ? 'Keep the club block' : 'Keep the club blocks',
      sub: one ? 'It stays as booked, marked Admin in All bookings.' : 'They stay as booked, marked Admin in All bookings.',
    },
    cancel: {
      title: one ? 'Cancel it' : 'Cancel them',
      sub: one ? 'It comes off the calendar.' : 'They come off the calendar.',
    },
  };
}

/**
 * F-FIX2 B12 (FT-M2M3: Settings read "Not set" while the calendar said "Times are in UK time"): the Settings tab's
 * Time zone words: the stored zone, else the zone the club runs on from where it is, else Not set.
 */
export function zoneSettingWord(settings) {
  if (settings?.timeZone) return settings.timeZoneLabel || settings.timeZone;
  const s = settings?.suggestedTimeZoneLabel || settings?.suggestedTimeZone;
  return s ? `${s} (from the club's location)` : 'Not set';
}

/**
 * X232 (R96 L1, L2): the Settings tab's Time zone select. While no zone is stored the club runs on the one from where it
 * is (the API's suggestedTimeZone), so the select shows it as its real value ("UK time (+00:00)", dark, never a faint
 * placeholder that is cut off), lifted to the top of the list, with "From the club's location" as a hint line under it.
 */
export function effectiveZone(settings) {
  return settings?.timeZone || settings?.suggestedTimeZone || null;
}

export function zoneFieldHint(settings) {
  return !settings?.timeZone && settings?.suggestedTimeZone ? "From the club's location" : '';
}

/**
 * X233 (R97 L-1): the text action in the hint line that stores the suggested zone ("Use UK time"); '' once a zone is
 * stored or when there is no suggestion.
 */
export function zoneUseWord(settings) {
  if (settings?.timeZone || !settings?.suggestedTimeZone) return '';
  return `Use ${settings.suggestedTimeZoneLabel || settings.suggestedTimeZone}`;
}

export function zoneFieldItems(options, settings) {
  const id = effectiveZone(settings);
  const list = options ?? [];
  if (!list.length) {
    // D1 B4: until the zones arrive the select shows the zone's label, never the raw id.
    if (!id) return [];
    const label = settings?.timeZone ? (settings.timeZoneLabel || id) : (settings?.suggestedTimeZoneLabel || id);
    return [{value: id, title: label, subtitle: id}];
  }
  if (settings?.timeZone || !id) return list;
  const top = list.find(o => o.value === id);
  return top ? [{...top, subtitle: `${top.subtitle} · From the club's location`}, ...list.filter(o => o.value !== id)] : list;
}

/**
 * What picking a zone does: 'none' (nothing picked, or the stored zone again), 'quiet' (the zone the club already runs
 * on, from its location: stored without asking, since no booking moves), 'check' (future bookings: the change dialog
 * lists how their times read), 'save'.
 */
export function zonePickAction(id, settings, futureBookings) {
  if (!id || id === settings?.timeZone) return 'none';
  if (id === effectiveZone(settings)) return 'quiet';
  return futureBookings > 0 ? 'check' : 'save';
}
